import React from 'react';
import { useStreamedText, sliceStream } from '../hooks/useStreamedText';

export interface AIResponse {
  introduction: string;
  beginning: string;
  process: string;
  result: string;
  summary: string;
}

interface AiResponseDisplayProps {
  response: AIResponse;
  /** Stream the reading in like ChatGPT. Off for restored/history readings. */
  animate?: boolean;
  typingSection?: keyof AIResponse | null;
  showSection?: {
    introduction: boolean;
    beginning: boolean;
    process: boolean;
    result: boolean;
    summary: boolean;
  };
}

const sections: Array<{
  key: keyof AIResponse;
  ordinal: string;
  label: string;
  sub: string;
  summary?: boolean;
}> = [
  { key: 'introduction', ordinal: '壹', label: '定体用', sub: '总论' },
  { key: 'beginning', ordinal: '贰', label: '本卦', sub: '当下' },
  { key: 'process', ordinal: '叁', label: '互卦', sub: '过程' },
  { key: 'result', ordinal: '肆', label: '变卦', sub: '结果' },
  { key: 'summary', ordinal: '伍', label: '结论与建议', sub: '可执行', summary: true },
];

export const AiResponseDisplay: React.FC<AiResponseDisplayProps> = ({
  response,
  animate = false,
  typingSection,
  showSection,
}) => {
  const visible = showSection || {
    introduction: true,
    beginning: true,
    process: true,
    result: true,
    summary: true,
  };

  const rendered = sections.filter((section) => visible[section.key] && response[section.key]);
  const texts = rendered.map((section) => String(response[section.key] || ''));
  const stream = useStreamedText(texts, animate);
  const { parts, active } = sliceStream(texts, stream.visible);

  if (rendered.length === 0) return null;

  return (
    <section className="mei-card reading-card">
      <header className="reading-head">
        <div>
          <div className="reading-eyebrow">卦象解读</div>
          <div className="reading-title">按梅花次第展开</div>
        </div>
        <div className="reading-head-actions">
          <span className={`reading-badge ${animate && !stream.done ? 'is-streaming' : ''}`}>
            {animate && !stream.done ? 'AI 生成中' : 'AI 生成'}
          </span>
          {animate && !stream.done && (
            <button type="button" className="stream-skip" onClick={stream.skip}>
              显示全部
            </button>
          )}
        </div>
      </header>

      <div className="reading-body">
        {rendered.map((section, index) => (
          <article
            key={section.key}
            className={`reading-section ${section.summary ? 'is-summary' : ''}`}
          >
            <div className="reading-rail" aria-hidden>
              <span className="reading-ordinal">{section.ordinal}</span>
              <span className="reading-line" />
            </div>
            <div className="reading-main">
              <div className="reading-section-head">
                <span className="reading-label">{section.label}</span>
                <span className="reading-sub">{section.sub}</span>
              </div>
              <div className="reading-content">
                {animate ? parts[index] : response[section.key]}
                {animate && !stream.done && active === index && (
                  <span className="stream-caret" aria-hidden />
                )}
                {!animate && typingSection === section.key && <span className="mei-typing-caret">|</span>}
              </div>
            </div>
          </article>
        ))}
      </div>

      <footer className="reading-foot">
        解读基于卦象结构生成，仅供传统文化研究与个人参考。
      </footer>

      {animate && (
        <span className="sr-only" aria-live="polite">
          {stream.done ? texts.join(' ') : ''}
        </span>
      )}
    </section>
  );
};
